import {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    type ReactNode,
} from 'react';
import { useRegistries, type RegistryProfile } from '@/hooks/use-registries';

interface RegistryContextValue {
    registries: RegistryProfile[];
    activeRegistryId: string | null;
    activeRegistry: RegistryProfile | null;
    setActiveRegistryId: (id: string) => void;
    isOnline: (registryId: string) => boolean;
    isLoading: boolean;
}

const RegistryContext = createContext<RegistryContextValue | null>(null);

export function RegistryProvider({ children }: { children: ReactNode }) {
    const { data: registries = [], isLoading } = useRegistries();
    const [activeRegistryId, setActiveId] = useState<string | null>(null);

    const defaultRegistry = registries.find((r) => r.is_default);
    const effectiveId = activeRegistryId ?? defaultRegistry?.id ?? null;

    const activeRegistry = useMemo(
        () => registries.find((r) => r.id === effectiveId) ?? null,
        [registries, effectiveId],
    );

    const setActiveRegistryId = useCallback((id: string) => {
        setActiveId(id);
    }, []);

    const isOnline = useCallback(
        (registryId: string) => {
            const reg = registries.find((r) => r.id === registryId);
            return reg?.status === 'active';
        },
        [registries],
    );

    const value = useMemo<RegistryContextValue>(
        () => ({
            registries,
            activeRegistryId: effectiveId,
            activeRegistry,
            setActiveRegistryId,
            isOnline,
            isLoading,
        }),
        [registries, effectiveId, activeRegistry, setActiveRegistryId, isOnline, isLoading],
    );

    return (
        <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>
    );
}

export function useRegistryContext() {
    const ctx = useContext(RegistryContext);
    if (!ctx) {
        throw new Error('useRegistryContext must be used within RegistryProvider');
    }
    return ctx;
}
